import produce from "immer";
import { createAction, createAsyncAction } from "typesafe-actions";
import { ActionType, createReducer } from "typesafe-actions";
import { APICallStatus, Comment, Violation, ViolationStatus } from "../../../Types/UnitFeatureTypes";


interface ViolationRequestState {
  request: {
    loading: boolean;
    success: boolean;
    updateStatusViolationApiStatus: APICallStatus;
    createViolationApiStatus: APICallStatus;
    error: string | null;
    violationId: string;
  };
}

export enum Types {
  CREATE_VIOLATION_REQUEST = 'CREATE_VIOLATION_REQUEST',
  CREATE_VIOLATION_SUCCESS = 'CREATE_VIOLATION_SUCCESS',
  CREATE_VIOLATION_FAILURE = 'CREATE_VIOLATION_FAILURE',
  UPDATE_VIOLATION_REQUEST = 'UPDATE_VIOLATION_REQUEST',
  UPDATE_VIOLATION_SUCCESS = 'UPDATE_VIOLATION_SUCCESS',
  UPDATE_VIOLATION_FAILURE = 'UPDATE_VIOLATION_FAILURE',
  UPDATE_STATUS_VIOLATION_REQUEST = 'UPDATE_STATUS_VIOLATION_REQUEST',
  UPDATE_STATUS_VIOLATION_SUCCESS = 'UPDATE_STATUS_VIOLATION_SUCCESS',
  UPDATE_STATUS_VIOLATION_FAILURE = 'UPDATE_STATUS_VIOLATION_FAILURE',
  CREATE_VIOLATION_COMMENT_REQUEST = 'CREATE_VIOLATION_COMMENT_REQUEST',
  CREATE_VIOLATION_COMMENT_SUCCESS = 'CREATE_VIOLATION_COMMENT_SUCCESS',
  CREATE_VIOLATION_COMMENT_FAILURE = 'CREATE_VIOLATION_COMMENT_FAILURE',
  DELETE_VIOLATION_COMMENT_REQUEST = 'DELETE_VIOLATION_COMMENT_REQUEST',
  DELETE_VIOLATION_COMMENT_SUCCESS = 'DELETE_VIOLATION_COMMENT_SUCCESS',
  DELETE_VIOLATION_COMMENT_FAILURE = 'DELETE_VIOLATION_COMMENT',
  RESET_STATUS_REQUEST = 'RESET_STATUS_REQUEST',
}

const createViolationRequestAction = createAsyncAction(
  Types.CREATE_VIOLATION_REQUEST,
  Types.CREATE_VIOLATION_SUCCESS,
  Types.CREATE_VIOLATION_FAILURE,
)
<
  { violationData: Violation, },
  { id: string },
  { message: string }
>();

const updateViolationRequestAction = createAsyncAction(
  Types.UPDATE_VIOLATION_REQUEST,
  Types.UPDATE_VIOLATION_SUCCESS,
  Types.UPDATE_VIOLATION_FAILURE,
)
<
  { violationData: Violation },
  void,
  { message: string }
>();

const updateStatusViolationRequestAction = createAsyncAction(
  Types.UPDATE_STATUS_VIOLATION_REQUEST,
  Types.UPDATE_STATUS_VIOLATION_SUCCESS,
  Types.UPDATE_STATUS_VIOLATION_FAILURE,
)
<
  { id: number, status: ViolationStatus },
  void,
  { message: string }
>();

const createViolationCommentRequestAction = createAsyncAction(
  Types.CREATE_VIOLATION_COMMENT_REQUEST,
  Types.CREATE_VIOLATION_COMMENT_SUCCESS,
  Types.CREATE_VIOLATION_COMMENT_FAILURE,
)
<
  { comment: Comment },
  void,
  { message: string }
>();

const deleteViolationCommentRequestAction = createAsyncAction(
  Types.DELETE_VIOLATION_COMMENT_REQUEST,
  Types.DELETE_VIOLATION_COMMENT_SUCCESS,
  Types.DELETE_VIOLATION_COMMENT_FAILURE,
)
<
  { commentId: number },
  void,
  { message: string }
>();

const resetStatusRequestAction = createAction(Types.RESET_STATUS_REQUEST)();

export const Actions = { createViolationRequestAction, updateViolationRequestAction, updateStatusViolationRequestAction, createViolationCommentRequestAction, deleteViolationCommentRequestAction, resetStatusRequestAction };

export const initialState: ViolationRequestState = {
  request: {
    loading: false,
    updateStatusViolationApiStatus: APICallStatus.PENDING,
    createViolationApiStatus: APICallStatus.PENDING,
    error: null as string | null,
    success: false,
    violationId: '',
  }
};

export type ViolationRequestInfoState = typeof initialState;

const createViolationRequestRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.success = false;
    draft.request.createViolationApiStatus = APICallStatus.PENDING;
    draft.request.error = null;

    return draft;
  });

const createViolationRequestSuccess = (
  state = initialState,
  { payload }: ActionType<typeof Actions.createViolationRequestAction.success>
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.success = true;
    if (draft.request.success) {
      draft.request.loading = false;
      draft.request.createViolationApiStatus = APICallStatus.SUCCESS;
      draft.request.violationId = payload.id;
    }

    return draft;
  });

const createViolationRequestFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.createViolationRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.success = false;
    draft.request.createViolationApiStatus = APICallStatus.FAILURE;
    draft.request.error = payload.message;

    return draft;
  });

const updatedViolationRequestRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.success = false;
    draft.request.error = null;

    return draft;
  });

const updatedViolationRequestSuccess = (
  state = initialState,
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.success = true;
    if (draft.request.success) {
      draft.request.loading = false;
    }

    return draft;
  });

const updatedViolationRequestFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.updateViolationRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.success = false;
    draft.request.error = payload.message;

    return draft;
  });

const updateStatusViolationRequestRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.updateStatusViolationApiStatus = APICallStatus.PENDING;
    draft.request.error = null;

    return draft;
  });

const updateStatusViolationRequestSuccess = (
  state = initialState,
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.loading = false;
    draft.request.updateStatusViolationApiStatus = APICallStatus.SUCCESS;

    return draft;
  });

const updateStatusViolationRequestFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.updateStatusViolationRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.updateStatusViolationApiStatus = APICallStatus.FAILURE;
    draft.request.error = payload.message;

    return draft;
  });

const createViolationCommentRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;
  });

const createViolationCommentSuccess = (
  state = initialState,
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.loading = false;
  });

const createViolationCommentFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.createViolationCommentRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = payload.message;
  });

const deleteViolationCommentRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;
  });

const deleteViolationCommentSuccess = (
  state = initialState,
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.loading = false;
  });

const deleteViolationCommentFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.deleteViolationCommentRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = payload.message;
  });

const resetStatusRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = null;
    draft.request.success = false;
    draft.request.updateStatusViolationApiStatus = APICallStatus.PENDING;
    draft.request.createViolationApiStatus = APICallStatus.PENDING;

    return draft;
  });


export default createReducer<typeof initialState, ActionType<typeof Actions>>(
  initialState
)
  .handleAction(Actions.createViolationRequestAction.request, createViolationRequestRequest)
  .handleAction(Actions.createViolationRequestAction.success, createViolationRequestSuccess)
  .handleAction(Actions.createViolationRequestAction.failure, createViolationRequestFailure)
  .handleAction(Actions.updateViolationRequestAction.request, updatedViolationRequestRequest)
  .handleAction(Actions.updateViolationRequestAction.success, updatedViolationRequestSuccess)
  .handleAction(Actions.updateViolationRequestAction.failure, updatedViolationRequestFailure)
  .handleAction(Actions.updateStatusViolationRequestAction.request, updateStatusViolationRequestRequest)
  .handleAction(Actions.updateStatusViolationRequestAction.success, updateStatusViolationRequestSuccess)
  .handleAction(Actions.updateStatusViolationRequestAction.failure, updateStatusViolationRequestFailure)
  .handleAction(Actions.createViolationCommentRequestAction.request, createViolationCommentRequest)
  .handleAction(Actions.createViolationCommentRequestAction.success, createViolationCommentSuccess)
  .handleAction(Actions.createViolationCommentRequestAction.failure, createViolationCommentFailure)
  .handleAction(Actions.deleteViolationCommentRequestAction.request, deleteViolationCommentRequest)
  .handleAction(Actions.deleteViolationCommentRequestAction.success, deleteViolationCommentSuccess)
  .handleAction(Actions.deleteViolationCommentRequestAction.failure, deleteViolationCommentFailure)
  .handleAction(Actions.resetStatusRequestAction, resetStatusRequest);
  